import React, { useState } from 'react';
import cc from 'classcat';
import { faBell, faMoon, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Container, Col, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import Flag from '../../molecules/flag';
import { SnoozeAlertAction } from '../action/snooze';
import { ResolveAlertAction } from '../action/resolve';
import { ViewPatientProgramAction } from '../action/view';
import Badge from '../badge';
import { RoomWrapper, RoomViewer } from '../room';
import Tooltip from '../tooltip';
import { IPatient } from '../../../libs/patient';
import { IAlert, IsAlertSnoozed, IsAlertResolved } from '../../../libs/alert';
import { AppKey } from '../../../libs/apps';
import { FormatDaysAndClassesAgo } from '../../../libs/time';
import { useApps } from '../../../contexts/apps';
import { useActiveDialogsUpdater } from '../../../contexts/active-dialogs';
import { useMetaData } from '../../../contexts/metadata';

interface ClickableAlertContainerProps {
  isEntireAlertClickable: boolean;
  patientId: number;
  alertApp: string;
}

const ClickableAlertContainer: React.FC<ClickableAlertContainerProps> = ({
  isEntireAlertClickable,
  patientId,
  alertApp,
  children
}) => {
  const addDialog = useActiveDialogsUpdater();
  const handleClick = () => {
    addDialog({
      id: `viewPatient${patientId}`,
      urlParam: `patientId=${patientId}&patientTab=${alertApp}`,
      name: 'ViewPatient',
      payload: {
        patientId: patientId,
        tabId: alertApp
      }
    });
  };
  /**
   * onClick handling via:
   * https://github.com/facebook/react/blob/edeea0720791f998b505f2ecdcf866c7e539e7a2/packages/react-dom/src/client/ReactDOMComponent.js#L234
   */
  return (
    <Container
      className="bg-white pt-4 pb-2 rounded border-1 border-light-grey border-solid"
      fluid
      onClick={isEntireAlertClickable ? handleClick : undefined}
    >
      {children}
    </Container>
  );
};

interface AlertProps {
  alert: IAlert;
  patient: IPatient;
  hideViewProgram?: boolean;
  inButton?: boolean;
  isEntireAlertClickable?: boolean; // Add click handler to entire alert
  isNarrow?: boolean;
  isSelectedInButton?: boolean;
  isEndedProgramAlert?: boolean;
  disableSnooze?: boolean;
}

/**
 * TODO:
 *  - Badge color intensity based on age
 *  - Show MM/DD after one week
 */
const Alert: React.FC<AlertProps> = ({
  alert,
  patient,
  hideViewProgram,
  inButton = false,
  isEntireAlertClickable = false,
  isNarrow = false,
  isSelectedInButton = false,
  disableSnooze = false
}) => {
  const { data } = useApps();
  const { isReadOnly, isEmbedded } = useMetaData();
  const isActionable = !isReadOnly && !isEmbedded;
  const [hasBeenSnoozed, setHasBeenSnoozed] = useState(IsAlertSnoozed(alert));
  const [snoozedUntil, setSnoozedUntil] = useState<string | null>(
    alert.snoozedUntil && alert.snoozedUntil instanceof Date
      ? alert.snoozedUntil.toISOString()
      : null
  );
  const isSnoozed = hasBeenSnoozed || IsAlertSnoozed(alert);
  const formattedAlert = FormatDaysAndClassesAgo(alert.createdAt);
  const [hasBeenResolved, setHasBeenResolved] = useState(
    IsAlertResolved(alert)
  );
  const isResolved = hasBeenResolved || IsAlertResolved(alert);
  const icon = isResolved ? faCheck : isSnoozed ? faMoon : faBell;
  // Default actions for alerts include Snooze, Resolve, and View Program Data
  let actions = isActionable ? (
    <React.Fragment>
      {!hideViewProgram ? (
        <ViewPatientProgramAction
          patient={patient}
          appKey={alert.app as AppKey}
          hideLabel={isNarrow}
          small
        />
      ) : null}
      <SnoozeAlertAction
        alert={alert}
        disabled={disableSnooze}
        onSnoozed={(until) => {
          setHasBeenSnoozed(true);
          setSnoozedUntil(until);
        }}
      />
      <ResolveAlertAction
        alert={alert}
        onResolved={() => setHasBeenResolved(true)}
      />
    </React.Fragment>
  ) : null;
  let badge: JSX.Element;
  let tooltipContent: JSX.Element;
  if (isResolved) {
    // If resolved, show resolved badge
    const formattedResolve = FormatDaysAndClassesAgo(
      alert.readAt || new Date()
    );
    badge = (
      <Badge>
        <Flag bgColor="light-green" className="text-small">
          <Icon icon={icon} className="text-green" />
        </Flag>
        <Text className="text-small text-gray1">{`${formattedAlert.naturalDate} at ${formattedAlert.time}`}</Text>
      </Badge>
    );
    tooltipContent = (
      <p className="text-sans-sm ma-0">
        Alert #{alert.id} resolved <br />
        {`${formattedResolve.date} at ${formattedResolve.time}`}
      </p>
    );
    actions = null;
  } else if (isSnoozed) {
    // If snoozed, show snoozed badge
    const formattedSnooze = FormatDaysAndClassesAgo(alert.snoozedAt);
    const formattedSnoozedUntil = FormatDaysAndClassesAgo(
      snoozedUntil ? new Date(snoozedUntil) : alert.snoozedUntil
    );
    badge = (
      <Badge>
        <Flag
          bgColor="light-orange"
          className={`${formattedAlert.className} text-small`}
        >
          <Icon icon={icon} className="text-brand" />
        </Flag>
        <Text className="text-small text-gray1">{`${formattedAlert.naturalDate} at ${formattedAlert.time}`}</Text>
      </Badge>
    );
    tooltipContent = (
      <p className="text-sans-sm ma-0">
        Alert #{alert.id} snoozed <br />
        {`${formattedSnooze.date} at ${formattedSnooze.time}`}
        <br />
        {`until ${formattedSnoozedUntil.date} ${formattedSnoozedUntil.time}`}
      </p>
    );
  } else {
    // Else, show date badge
    badge = (
      <Badge>
        <Flag
          className={`${formattedAlert.className} bg-light-blue text-small`}
        >
          <Icon icon={icon} className="text-blue3 mr-1" />
          <Text bold color="black">
            {formattedAlert.naturalDate}
          </Text>
        </Flag>
        <Text className="text-small text-gray1">{formattedAlert.time}</Text>
      </Badge>
    );
    tooltipContent = (
      <p className="text-sans-sm ma-0">
        Alert #{alert.id} triggered <br />
        {`${formattedAlert.date} at ${formattedAlert.time}`}
      </p>
    );
  }

  // Alerts are only in buttons in the <RecentIssues /> component inside <AddressEvents />
  if (inButton) {
    const inButtonClasses = cc([
      'bg-white rounded hover-shadow border-1 border-solid overflow-hidden',
      {
        'border-light-grey': !isSelectedInButton,
        'border-light-green': isSelectedInButton
      }
    ]);
    return (
      <Container className={inButtonClasses} fluid>
        {isSelectedInButton && (
          <Row className="py-1 text-small text-green bg-light-green2">
            <Col>
              <Icon className="mr-1" icon={faCheck} /> Selected
            </Col>
          </Row>
        )}
        <Row className="pt-4">
          <Col className="pa-0-important">
            <Tooltip id="tltp-time-1 nowrap" content={tooltipContent}>
              <span role="status">{badge}</span>
              <span className="text-small text-gray1">&nbsp;#{alert.id}</span>
            </Tooltip>
          </Col>
          <Col className="text-right nowrap pa-0-important">
            <span className="pr-4 text-small text-gray1">
              {data.apps[alert.app as AppKey].name}
            </span>
          </Col>
        </Row>
        <div className="mt-4 ml-3 mr-3 mb-3">
          <Text
            bold
            spacing="mr-2"
            size="sm"
          >{`${patient.firstName} ${patient.lastName}`}</Text>
          <Text size="sm">{alert.body}</Text>
        </div>
      </Container>
    );
  }

  if (hideViewProgram) {
    return (
      <div className="my-4 rounded">
        <ClickableAlertContainer
          isEntireAlertClickable={isActionable && isEntireAlertClickable}
          patientId={patient.id}
          alertApp={alert.app}
        >
          <Row>
            <Col className="pa-0-important">
              <Tooltip id="tltp-time-1 nowrap" content={tooltipContent}>
                <span role="status">{badge}</span>
                <span className="text-small text-gray1">&nbsp;#{alert.id}</span>
              </Tooltip>
            </Col>
            <Col className="text-right pa-0-important nowrap" xs="content">
              {actions}
            </Col>
          </Row>
          <div className="mt-2 ml-3 mr-3 mb-3">
            <Text
              bold
              spacing="mr-1"
              size="sm"
            >{`${patient.firstName} ${patient.lastName}`}</Text>
            <Text size="sm">{alert.body}</Text>
          </div>
        </ClickableAlertContainer>
      </div>
    );
  }
  if (!patient) return null;

  return (
    <RoomWrapper
      patientId={patient.id}
      className="hover-shadow my-4 rounded"
      showTooltip
    >
      <ClickableAlertContainer
        isEntireAlertClickable={isActionable && isEntireAlertClickable}
        patientId={patient.id}
        alertApp={alert.app}
      >
        <Row>
          <Col className="pr-0-important" xs="content">
            <RoomViewer patientId={patient.id} className="mr-2" />
          </Col>
          <Col className="pa-0-important">
            <Tooltip id="tltp-time-1 nowrap" content={tooltipContent}>
              <span role="status">{badge}</span>
              <span className="text-small text-gray1">&nbsp;#{alert.id}</span>
            </Tooltip>
          </Col>
          <Col className="text-right pa-0-important nowrap" xs="content">
            {actions}
          </Col>
        </Row>
        <div className="mt-2 ml-3 mr-3 mb-3">
          <Text
            bold
            spacing="mr-1"
            size="sm"
          >{`${patient.firstName} ${patient.lastName}`}</Text>
          <Text size="sm">{alert.body}</Text>
        </div>
      </ClickableAlertContainer>
    </RoomWrapper>
  );
};

/**
 * Performance optimization to improve the render performance of
 * the dashboard. Using React.memo always adds additional complexity
 * so use sparingly.
 */
const MemoizedAlert = React.memo(Alert, (prevProps, nextProps) => {
  /**
   * If we have the same alert, and that alert has the same
   * resolution status and same snooze status, then return true (meaning don't re-render)
   */
  const isSameAlertId = prevProps.alert.id === nextProps.alert.id;
  const isSameResolveStatus =
    IsAlertResolved(prevProps.alert) === IsAlertResolved(nextProps.alert);
  const isSameSnoozeStatus =
    IsAlertSnoozed(prevProps.alert) === IsAlertSnoozed(nextProps.alert);
  const isSameSelectedInButton =
    prevProps.isSelectedInButton === nextProps.isSelectedInButton;
  return (
    isSameAlertId &&
    isSameResolveStatus &&
    isSameSnoozeStatus &&
    isSameSelectedInButton
  );
});

export default MemoizedAlert;
